<template>
  <b-modal ref="modal" id="barcode-labels-modal" size="lg" hide-footer scrollable @hidden="reset">
    <template #modal-title>
      <span class="text-primary text-weight-700">Print Barcodes</span>
    </template>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="!rows.length" class="text-center text-muted py-5">
      No items to label. Services don't get barcode labels.
    </div>

    <div v-else>
      <!-- Warnings -->
      <div v-if="missingCount" class="alert alert-danger py-2">
        <i class="bi bi-exclamation-circle mr-1"></i>
        {{ missingCount }} item{{ missingCount === 1 ? ' has' : 's have' }} no barcode yet and will be skipped.
        <span v-if="canGenerate">Use <strong>Generate</strong> next to the item.</span>
      </div>
      <div v-if="duplicateCount" class="alert alert-danger py-2">
        <i class="bi bi-exclamation-triangle mr-1"></i>
        {{ duplicateCount }} item{{ duplicateCount === 1 ? ' shares its' : 's share their' }} barcode with another item —
        a scan may add the wrong product. Generate a new barcode or edit the SKU.
      </div>

      <!-- Settings -->
      <div class="form-row">
        <div class="form-group col-md-7">
          <label>Label size</label>
          <select v-model="preset" class="custom-select">
            <option v-for="(p, key) in presets" :key="key" :value="key">{{ p.label }}</option>
          </select>
        </div>
        <div v-if="rows.length > 1" class="form-group col-md-5">
          <label>Labels per item</label>
          <div class="input-group">
            <input v-model.number="bulkQty" type="number" min="0" class="form-control" />
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" type="button" @click="applyBulkQty">Apply to all</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 80 mm receipt printer with a sticker roll: measure and adjust -->
      <div v-if="isRoll" class="border rounded p-3 mb-3">
        <p class="mb-2"><strong>Sticker size on your roll</strong>
          <small class="text-muted d-block">Measure one sticker with a ruler. Saved on this device.</small>
        </p>
        <div class="form-row">
          <div class="form-group col-6 col-md-3">
            <label>Width (mm)</label>
            <input v-model.number="roll.w" type="number" min="20" max="80" step="0.5" class="form-control" />
          </div>
          <div class="form-group col-6 col-md-3">
            <label>Height (mm)</label>
            <input v-model.number="roll.h" type="number" min="10" max="150" step="0.5" class="form-control" />
          </div>
          <div class="form-group col-6 col-md-3">
            <label>Gap between (mm)</label>
            <input v-model.number="roll.gap" type="number" min="0" max="20" step="0.5" class="form-control" />
          </div>
          <div class="form-group col-6 col-md-3">
            <label>Shift right (mm)</label>
            <input v-model.number="roll.offset" type="number" min="-20" max="20" step="0.5" class="form-control" />
          </div>
        </div>
        <small class="text-muted d-block">
          In the print window: pick your receipt printer, set <strong>Margins: None</strong> and <strong>Scale: 100%</strong> (Default),
          and turn off <strong>Headers and footers</strong>. If labels slowly drift off the stickers, adjust <strong>Gap between</strong>;
          if they sit off to one side, adjust <strong>Shift right</strong> (negative moves left).
        </small>
      </div>

      <!-- Items -->
      <b-table
        :items="rows"
        :fields="fields"
        :per-page="perPage"
        :current-page="page"
        small
        responsive="sm"
        hover
        :tbody-tr-class="rowClass"
        @row-clicked="(row) => (previewId = row.key)"
      >
        <template #cell(name)="data">
          <div>{{ data.item.name }}</div>
          <small class="text-muted">{{ formatPrice(data.item.price) }}</small>
        </template>

        <template #cell(barcode)="data">
          <div v-if="data.item.barcode">
            <span class="text-monospace">{{ data.item.barcode }}</span>
            <small class="text-muted d-block">{{ formatLabel(data.item.format) }}</small>
            <small v-if="data.item.is_duplicate" class="text-danger d-block">Shared with another item</small>
            <small v-if="!data.item.svg" class="text-danger d-block">Can't be encoded as a barcode</small>
          </div>
          <span v-else class="text-danger">No barcode</span>

          <button
            v-if="canGenerate && (data.item.needs_code || data.item.is_duplicate)"
            class="btn btn-link p-0 d-block"
            :disabled="data.item.generating"
            @click.stop="generateForRow(data.item)"
          >{{ data.item.generating ? 'Generating…' : 'Generate barcode' }}</button>
        </template>

        <template #cell(qty)="data">
          <input
            v-model.number="data.item.qty"
            type="number"
            min="0"
            class="form-control"
            style="width: 90px"
            :disabled="!data.item.svg"
            @click.stop
          />
        </template>
      </b-table>
      <b-pagination
        v-if="rows.length > perPage"
        v-model="page"
        :total-rows="rows.length"
        :per-page="perPage"
        align="center"
      />

      <!-- Preview -->
      <div v-if="previewRow" class="my-3">
        <p class="mb-1"><strong>Preview</strong> <small class="text-muted">— actual size, click an item to preview it</small></p>
        <div class="bl-preview-wrap">
          <div class="bl-preview-label" v-html="previewHtml"></div>
        </div>
        <small v-if="previewTooDense" class="text-danger d-block mt-1">
          This barcode is too long for {{ layout.w }} mm and may not scan reliably. Pick a wider label or generate a shorter barcode.
        </small>
      </div>

      <div class="d-flex justify-content-between align-items-center border-top pt-3">
        <span class="text-muted">
          {{ totalLabels }} label{{ totalLabels === 1 ? '' : 's' }}<span v-if="pageCount"> on {{ pageCount }} A4 page{{ pageCount === 1 ? '' : 's' }}</span>
        </span>
        <div>
          <button class="btn btn-outline-secondary" @click="$refs.modal.hide()">Cancel</button>
          <button class="btn btn-primary ml-2" :disabled="!totalLabels" @click="print">
            <i class="bi bi-printer mr-1"></i> Print
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import {
  LABEL_PRESETS,
  DEFAULT_PRESET,
  ROLL80_DEFAULTS,
  resolvePreset,
  MIN_MODULE_MM,
  renderBarcodeSvg,
  moduleSizeMm,
  formatLabelPrice,
  labelHtml,
  labelCss,
  buildPrintDocument,
  printDocument,
} from '../../services/barcodeLabels';

const PRESET_STORAGE_KEY = 'barcodeLabelPreset';
const ROLL_STORAGE_KEY = 'barcodeRoll80';

export default {
  name: 'BarcodeLabelsModal',
  props: {
    // Admin / "can manage inventory" — may replace SKUs with generated codes.
    canGenerate: { type: Boolean, default: false },
  },
  data() {
    let preset = DEFAULT_PRESET;
    try {
      const saved = window.localStorage.getItem(PRESET_STORAGE_KEY);
      if (saved && LABEL_PRESETS[saved]) preset = saved;
    } catch (e) { /* storage unavailable */ }

    let roll = { ...ROLL80_DEFAULTS };
    try {
      roll = { ...roll, ...JSON.parse(window.localStorage.getItem(ROLL_STORAGE_KEY) || '{}') };
    } catch (e) { /* storage unavailable */ }

    return {
      presets: LABEL_PRESETS,
      preset,
      roll,
      loading: false,
      businessName: '',
      rows: [],
      bulkQty: 1,
      previewId: null,
      page: 1,
      perPage: 25,
      fields: [
        { key: 'name', label: 'Item' },
        { key: 'barcode', label: 'Barcode' },
        { key: 'qty', label: 'Labels' },
      ],
    };
  },
  computed: {
    ...mapGetters(['userDetails']),
    isRoll() {
      return !!this.presets[this.preset].continuous;
    },
    // The size actually printed: a fixed preset, or the roll with the user's measurements.
    layout() {
      return resolvePreset(this.preset, this.roll);
    },
    currency() {
      return this.userDetails?.company?.companysettings?.currency;
    },
    missingCount() {
      return this.rows.filter((r) => !r.barcode).length;
    },
    duplicateCount() {
      return this.rows.filter((r) => r.is_duplicate).length;
    },
    printableRows() {
      return this.rows.filter((r) => r.svg && r.qty > 0);
    },
    totalLabels() {
      return this.printableRows.reduce((sum, r) => sum + Math.floor(r.qty), 0);
    },
    pageCount() {
      const sheet = this.presets[this.preset].sheet;
      return sheet ? Math.ceil(this.totalLabels / (sheet.cols * sheet.rows)) : 0;
    },
    previewRow() {
      return this.rows.find((r) => r.key === this.previewId && r.svg) || this.rows.find((r) => r.svg) || null;
    },
    // Same markup + CSS as the printout. Every selector is .bl-* prefixed,
    // so injecting the <style> here can't leak onto other page elements.
    previewHtml() {
      return this.previewRow ? `<style>${labelCss(this.layout)}</style>${this.labelFor(this.previewRow)}` : '';
    },
    previewTooDense() {
      const mm = this.previewRow && moduleSizeMm(this.previewRow.svg, this.layout);
      return mm !== null && mm < MIN_MODULE_MM;
    },
  },
  watch: {
    preset(value) {
      try { window.localStorage.setItem(PRESET_STORAGE_KEY, value); } catch (e) { /* storage unavailable */ }
    },
    roll: {
      deep: true,
      handler(value) {
        try { window.localStorage.setItem(ROLL_STORAGE_KEY, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
      },
    },
  },
  methods: {
    /** Open for specific items, or every item in the company with { all: true }. */
    async open({ productIds = [], groupedIds = [], all = false } = {}) {
      this.reset();
      this.loading = true;
      this.$refs.modal.show();
      try {
        const { data } = await axios.post('/api/barcodes/labels', {
          all,
          product_ids: productIds,
          grouped_ids: groupedIds,
        });
        this.businessName = data.business_name;
        const defaultQty = data.labels.length === 1 ? 1 : this.bulkQty;
        this.rows = data.labels.map((l) => this.toRow(l, defaultQty));
      } catch (e) {
        this.$toasts.push({ type: 'error', message: e.response?.data?.message || 'Could not load barcode labels.' });
        this.$refs.modal.hide();
      } finally {
        this.loading = false;
      }
    },

    toRow(label, qty) {
      const svg = renderBarcodeSvg(label.barcode, label.format);
      return { ...label, key: label.type + ':' + label.id, svg, qty: svg ? qty : 0, generating: false };
    },

    labelFor(row) {
      return labelHtml({
        businessName: this.businessName,
        name: row.name,
        price: this.formatPrice(row.price),
        barcode: row.barcode,
        svg: row.svg,
      });
    },

    formatPrice(amount) {
      return formatLabelPrice(amount, this.currency);
    },

    formatLabel(format) {
      return { EAN13: 'EAN-13 (manufacturer)', EAN8: 'EAN-8 (manufacturer)', UPC: 'UPC-A (manufacturer)' }[format] || 'Code 128 (internal)';
    },

    rowClass(row) {
      return row && this.previewRow && row.key === this.previewRow.key ? 'table-active' : '';
    },

    applyBulkQty() {
      const qty = Math.max(0, Math.floor(this.bulkQty || 0));
      this.rows.forEach((r) => { if (r.svg) r.qty = qty; });
    },

    print() {
      const labels = [];
      this.printableRows.forEach((row) => {
        const html = this.labelFor(row);
        for (let i = 0; i < Math.floor(row.qty); i++) labels.push(html);
      });
      printDocument(buildPrintDocument(this.layout, labels));
    },

    async generateForRow(row) {
      const sku = await this.generateItem({
        productId: row.type === 'product' ? row.id : null,
        groupedId: row.type === 'grouped' ? row.id : null,
        name: row.name,
        currentSku: row.barcode,
      });
      if (!sku) return;
      const updated = this.toRow({ ...row, barcode: sku, format: 'CODE128', needs_code: false, is_duplicate: false }, row.qty || 1);
      Object.assign(row, updated);
    },

    /**
     * Single item. Returns the new SKU, or null if cancelled/failed.
     * Also used directly from an item's More menu.
     */
    async generateItem({ productId = null, groupedId = null, name = 'this item', currentSku = null }) {
      const replaces = currentSku
        ? `Its current SKU "${currentSku}" will be replaced, and labels already printed for it will stop scanning.`
        : 'It has no barcode yet.';
      const ok = await this.$bvModal.msgBoxConfirm(
        `Generate a new barcode for ${name}? ${replaces} Manufacturer barcodes (EAN/UPC) are never replaced.`,
        { title: 'Generate Barcode', okTitle: 'Generate', okVariant: 'primary', cancelVariant: 'outline-secondary', centered: true },
      );
      if (!ok) return null;

      try {
        const { data } = await axios.post('/api/barcodes/generate', productId ? { product_id: productId } : { grouped_id: groupedId });
        this.$toasts.push({
          type: 'success',
          message: data.sku === currentSku ? `${name} already has a valid barcode.` : `New barcode for ${name}: ${data.sku}`,
        });
        this.$emit('generated');
        return data.sku;
      } catch (e) {
        this.showError(e);
        return null;
      }
    },

    /** Whole company. Used from the Inventory page's More menu. */
    async generateAll() {
      let status;
      try {
        ({ data: status } = await axios.get('/api/barcodes/status'));
      } catch (e) {
        this.showError(e);
        return;
      }

      if (status.integration_blocked) {
        this.$bvModal.msgBoxOk(
          'Your online store (Shopify/WooCommerce) has synced and matches orders to items by SKU, so barcodes can\'t be regenerated while it is connected. You can still print labels with your current SKUs.',
          { title: 'Generate Barcodes', okVariant: 'primary', centered: true },
        );
        return;
      }

      const total = status.pending.products + status.pending.grouped;
      if (!total) {
        this.$bvModal.msgBoxOk('Every item already has a standard or manufacturer barcode.', {
          title: 'Generate Barcodes', okVariant: 'primary', centered: true,
        });
        return;
      }

      const h = this.$createElement;
      const message = h('div', [
        h('p', `${total} item${total === 1 ? '' : 's'} will get a new 10-digit barcode, replacing their current SKU.`),
        h('ul', { class: 'pl-3' }, [
          h('li', 'Manufacturer barcodes (EAN/UPC) and items that already have a standard code are kept.'),
          h('li', 'Labels already printed for the replaced items will stop scanning.'),
          h('li', 'Every change is recorded in each item\'s change history.'),
        ]),
      ]);
      const ok = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Generate Barcodes for All Items',
        okTitle: `Generate ${total}`,
        okVariant: 'danger',
        cancelVariant: 'outline-secondary',
        centered: true,
      });
      if (!ok) return;

      try {
        const { data } = await axios.post('/api/barcodes/generate', { all: true });
        const n = data.updated.products + data.updated.grouped;
        this.$toasts.push({ type: 'success', message: `Generated ${n} new barcode${n === 1 ? '' : 's'}.` });
        this.$emit('generated');
      } catch (e) {
        this.showError(e);
      }
    },

    showError(e) {
      this.$toasts.push({ type: 'error', message: e.response?.data?.message || 'Something went wrong. Please try again.' });
    },

    reset() {
      this.rows = [];
      this.businessName = '';
      this.previewId = null;
      this.page = 1;
    },
  },
};
</script>

<style scoped>
.bl-preview-wrap {
  display: inline-block;
  padding: 12px;
  background: #f1f3f5;
  border-radius: 4px;
}
.bl-preview-label {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  background: #fff;
}
</style>
